import { useSession } from "../hooks/useSession.js";
import { ApiError, api } from "../lib/api.js";
import { itemDrafts } from "../lib/itemDrafts.js";
import { useEffect, useRef, useState, type ChangeEvent } from "react";
import {
  normalizeFunkoFeatures,
  ITEM_HAZMAT_VALUES,
  flattenInventoryCategories,
  inventoryCategoryPair,
  defaultInventoryCategoryId,
  POP_WEIGHTS,
  SALE_PLATFORMS,
  WHATNOT_DEFAULTS,
  whatnotConditions,
  isWhatnotCondition,
  retainWhatnotCondition,
  type WhatnotOverrides,
} from "@farnsworth/shared";
import {
  AutocompleteInput,
  Badge,
  Button,
  Card,
  Checkbox,
  ErrorBanner,
  Field,
  Input,
  Modal,
  Section,
  Select,
  Textarea,
} from "./ui.js";
import {
  useCreateItem,
  useItemSuggestions,
  useFunkoDetails,
  useRefreshGtPoppediaValuation,
  useUpdateItem,
} from "../hooks/useInventory.js";
import { FeatureMultiSelect } from "./FeatureMultiSelect.js";
import { GtPoppediaPicker } from "./GtPoppediaPicker.js";
import { uploadItemImage } from "../lib/api.js";
import { useQueryClient } from "@tanstack/react-query";
import { today } from "../lib/format.js";
import type { Category, ForSaleListing, Item } from "../types.js";

interface FormState {
  name: string;
  sku: string;
  barcode: string;
  categoryId: string;
  description: string;
  license: string;
  series: string;
  exclusiveTo: string;
  boxNo: string;
  popWeight: string;
  location: string;
  owner: string;
  condition: string;
  hazmat: string;
  lastChecked: string;
  purchasePrice: string;
  currentValue: string;
  forSalePrice: string;
  soldPrice: string;
  sold: boolean;
  soldDate: string;
  soldWhere: string;
  quantity: string;
  isWebsiteFeatured: boolean;
  listings: ForSaleListing[];
  images: string[];
}

interface FunkoState {
  features: string[];
  ppgPrice: string;
  isVaulted: boolean;
  isChase: boolean;
}

const EMPTY_FUNKO: FunkoState = {
  features: [],
  ppgPrice: "",
  isVaulted: false,
  isChase: false,
};

/** Only the Whatnot columns that are genuine per-item overrides. */
type WhatnotState = Record<keyof WhatnotOverrides, string>;

const DEFAULT_WHATNOT: WhatnotState = {
  type: WHATNOT_DEFAULTS.type,
  offerable: WHATNOT_DEFAULTS.offerable,
};

/**
 * Editor fields start from the shared defaults so a new item needs no typing,
 * then are overlaid with anything already stored for the item.
 */
function toWhatnot(metadata: Record<string, unknown> | undefined): WhatnotState {
  const raw = metadata?.whatnot;
  const source = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};

  const read = (key: keyof WhatnotState): string => {
    const value = source[key];
    return typeof value === "string" && value.trim() !== "" ? value : DEFAULT_WHATNOT[key];
  };

  return {
    type: read("type"),
    offerable: read("offerable"),
  };
}

function toForm(item: Item | null, categories: Category[]): FormState {
  return {
    name: item?.name ?? "",
    sku: item?.sku ?? "",
    barcode: item?.barcode ?? "",
    categoryId: item ? item.categoryId ?? "" : defaultInventoryCategoryId(categories) ?? "",
    description: item?.description ?? "",
    license: item?.license ?? "",
    series: item?.series ?? "",
    exclusiveTo: item?.exclusiveTo ?? "",
    boxNo: item?.boxNo ?? "",
    // Shipping weight and the Whatnot shipping profile are the same thing, so
    // this one field drives both. 8-11 oz is the house default.
    popWeight: item?.popWeight ?? "8-11 oz",
    location: item?.location ?? "",
    owner: item?.owner ?? "",
    condition: item ? item.condition ?? "" : "Good",
    hazmat: item?.hazmat ?? "Not Hazmat",
    lastChecked: item ? item.lastChecked ?? item.createdAt.slice(0, 10) : today(),
    purchasePrice: item?.purchasePrice ?? "",
    currentValue: item?.currentValue ?? "",
    forSalePrice: item?.forSalePrice ?? "",
    soldPrice: item?.soldPrice ?? "",
    sold: item?.sold ?? false,
    soldDate: item?.soldDate ?? "",
    soldWhere: item?.soldWhere ?? "",
    quantity: String(item?.quantity ?? 1),
    isWebsiteFeatured: item?.isWebsiteFeatured ?? false,
    listings: item?.forSaleListings ?? [],
    images: item?.imageUrls ?? [],
  };
}

const nullIfEmpty = (value: string): string | null => (value.trim() === "" ? null : value.trim());

export function ItemEditor({
  item,
  categories,
  suggestions = { license: [], series: [], exclusiveTo: [], location: [] },
  onClose,
}: {
  item: Item | null;
  categories: Category[];
  suggestions?: { license:string[]; series:string[]; exclusiveTo:string[]; location:string[] };
  onClose: () => void;
}) {
  const owner = useSession().data?.id ?? "";
  const draftKey = `${owner}:${item?.id ?? "new"}`;
  const initialDraft = useRef(itemDrafts.get(draftKey) as {
    form: FormState; funko: FunkoState; whatnot: WhatnotState; pendingImage: File | null;
    version: number | undefined; parentCategoryId: string; createStep: number; createdItem: Item | null;
  } | undefined).current;
  const dirty = useRef(Boolean(initialDraft));
  const [baseVersion, setBaseVersion] = useState(initialDraft?.version ?? item?.version);
  const [latestItem, setLatestItem] = useState<Item | null>(null);
  const [form, setForm] = useState<FormState>(() => initialDraft?.form ?? toForm(item, categories));
  const licenseSuggestions = useItemSuggestions(form.license);
  const seriesSuggestions = useItemSuggestions(form.series);
  const retailerSuggestions = useItemSuggestions(form.exclusiveTo);
  const locationSuggestions = useItemSuggestions(form.location);
  const [funko, setFunko] = useState<FunkoState>(initialDraft?.funko ?? EMPTY_FUNKO);
  const [funkoExists, setFunkoExists] = useState(false);
  const [whatnot, setWhatnot] = useState<WhatnotState>(() => initialDraft?.whatnot ?? toWhatnot(item?.metadata));
  useEffect(() => {
    if (item || !categories.length) return;
    const defaultId = defaultInventoryCategoryId(categories);
    if (!defaultId) return;
    setForm((previous) => previous.categoryId ? previous : { ...previous, categoryId: defaultId });
    setParentCategoryId((previous) => previous || flattenInventoryCategories(categories).find((entry) => entry.id === defaultId)?.parentId || "");
  }, [categories, item]);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [showGtPicker, setShowGtPicker] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [createStep, setCreateStep] = useState(initialDraft?.createStep ?? 0);
  const [pendingImage, setPendingImage] = useState<File | null>(initialDraft?.pendingImage ?? null);
  const [createdItem, setCreatedItem] = useState<Item | null>(initialDraft?.createdItem ?? null);
  const activeItem = item ?? createdItem;
  const flatCategories = flattenInventoryCategories(categories);
  const selectedCategory = flatCategories.find((entry) => entry.id === form.categoryId);
  const [parentCategoryId, setParentCategoryId] = useState(() => {
    const initial = flatCategories.find((entry) => entry.id === form.categoryId);
    return initialDraft?.parentCategoryId ?? initial?.parentId ?? initial?.id ?? "";
  });
  const categoryPair = inventoryCategoryPair(categories, form.categoryId);
  const showFunko = ["Funko Pop!", "Funko Soda"].includes(categoryPair.subCategory);
  const parentName = flatCategories.find((entry) => entry.id === parentCategoryId)?.name ?? "";
  const conditionOptions = whatnotConditions(categoryPair.category || parentName, categoryPair.subCategory);

  function changeCategory(categoryId: string, parentId: string) {
    const pair = inventoryCategoryPair(categories, categoryId);
    setParentCategoryId(parentId);
    setForm((previous) => ({ ...previous, categoryId,
      condition: retainWhatnotCondition(pair.category, pair.subCategory, previous.condition) }));
  }
  const queryClient = useQueryClient();

  const createItem = useCreateItem();
  const updateItem = useUpdateItem();
  const refreshValuation = useRefreshGtPoppediaValuation();
  const funkoQuery = useFunkoDetails(activeItem?.id);

  useEffect(() => {
    if (!funkoQuery.data || dirty.current) return;
    setFunko({
      features: normalizeFunkoFeatures(funkoQuery.data.features ?? (funkoQuery.data.stickerType ? [funkoQuery.data.stickerType] : [])),
      ppgPrice: funkoQuery.data.ppgPrice ?? "",
      isVaulted: funkoQuery.data.isVaulted,
      isChase: funkoQuery.data.isChase,
    });
    setFunkoExists(true);
  }, [funkoQuery.data]);

  // Existing items upload as soon as a file is picked. New items keep the
  // chosen file until create returns an id, then upload it within this flow.
  // In both cases the response's imageUrls becomes the form's truth so a
  // later save cannot remove an image that was just uploaded.
  async function handleUpload(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = ""; // allow re-picking the same file
    if (!file) return;

    if (!activeItem) {
      setPendingImage(file);
      setUploadError(null);
      return;
    }

    setUploading(true);
    setUploadError(null);
    try {
      const { imageUrls, version } = await uploadItemImage(activeItem.id, file);
      setBaseVersion(previous => previous !== undefined && version === previous + 1 ? version : previous);
      update("images", imageUrls);
      void queryClient.invalidateQueries({ queryKey: ["items"] });
      void queryClient.invalidateQueries({ queryKey: ["item", activeItem.id] });
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : "Image upload failed.");
    } finally {
      setUploading(false);
    }
  }

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    dirty.current = true;
    setForm((previous) => ({ ...previous, [key]: value }));
  }

  function updateListing(index: number, patch: Partial<ForSaleListing>) {
    dirty.current = true;
    setForm((previous) => ({
      ...previous,
      listings: previous.listings.map((entry, i) => (i === index ? { ...entry, ...patch } : entry)),
    }));
  }

  const busy = createItem.isPending || updateItem.isPending || uploading;
  const guided = activeItem === null;
  const steps = ["Identify", "Stock & value", "Selling", "Review"];
  const showStep = (step: number) => !guided || createStep === step;
  const mutationError = createItem.error ?? updateItem.error;

  useEffect(() => {
    if (dirty.current) itemDrafts.set(draftKey, { form, funko, whatnot, pendingImage,
      version: baseVersion, parentCategoryId, createStep, createdItem });
  });
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (dirty.current) { event.preventDefault(); event.returnValue = ""; }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, []);
  function requestClose() {
    if (busy) return;
    if (dirty.current && !window.confirm("Discard your unsaved item changes?")) return;
    itemDrafts.delete(draftKey);
    onClose();
  }
  const conflicted = updateItem.error instanceof ApiError && updateItem.error.status === 409;

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setValidationError(null);
    const addAnother = (event.nativeEvent as SubmitEvent).submitter?.getAttribute("data-action") === "add-another";

    if (!form.name.trim()) {
      setValidationError("Name is required.");
      return;
    }

    if (form.condition && !conditionOptions.includes(form.condition)) {
      setValidationError("Choose a condition accepted for the selected category.");
      return;
    }

    const quantity = Number.parseInt(form.quantity || "0", 10);
    if (Number.isNaN(quantity) || quantity < 0) {
      setValidationError("Quantity must be a whole number of 0 or more.");
      return;
    }

    const payload: Record<string, unknown> = {
      name: form.name.trim(),
      sku: nullIfEmpty(form.sku),
      barcode: nullIfEmpty(form.barcode),
      categoryId: form.categoryId || null,
      description: nullIfEmpty(form.description),
      license: nullIfEmpty(form.license),
      series: nullIfEmpty(form.series),
      exclusiveTo: nullIfEmpty(form.exclusiveTo),
      boxNo: nullIfEmpty(form.boxNo),
      popWeight: nullIfEmpty(form.popWeight),
      location: nullIfEmpty(form.location),
      owner: nullIfEmpty(form.owner),
      condition: form.condition || null,
      hazmat: form.hazmat,
      lastChecked: form.lastChecked || null,
      purchasePrice: nullIfEmpty(form.purchasePrice),
      currentValue: nullIfEmpty(form.currentValue),
      forSalePrice: nullIfEmpty(form.forSalePrice),
      soldPrice: nullIfEmpty(form.soldPrice),
      sold: form.sold,
      soldDate: form.soldDate || null,
      soldWhere: form.soldWhere || null,
      forSaleListings: form.listings.filter((entry) => entry.platform.trim() || entry.url.trim()),
      quantity,
      isWebsiteFeatured: form.isWebsiteFeatured,
      imageUrls: form.images.map((url) => url.trim()).filter(Boolean),
    };

    // Merge into existing metadata so unrelated keys (such as the legacy
    // import's `legacyId`) survive a save. Only genuine deviations from the
    // shared defaults are stored, so the defaults can evolve later.
    const metadata: Record<string, unknown> = { ...(activeItem?.metadata ?? {}) };
    const whatnotValues: Record<string, string> = {};
    // Remove old duplicated listing fields; inventory owns these values now.
    for (const [key, value] of Object.entries(whatnot) as Array<[keyof WhatnotState, string]>) {
      const trimmed = value.trim();
      if (trimmed !== "" && trimmed !== DEFAULT_WHATNOT[key]) {
        whatnotValues[key] = trimmed;
      }
    }
    if (Object.keys(whatnotValues).length > 0) {
      metadata.whatnot = whatnotValues;
    } else {
      delete metadata.whatnot;
    }
    payload.metadata = metadata;

    const hasFunkoValues =
      funko.features.length > 0 ||
      funko.ppgPrice.trim() !== "" ||
      funko.isVaulted ||
      funko.isChase;

    if (showFunko && (hasFunkoValues || funkoExists)) {
      payload.funko = { features: funko.features, ppgPrice: nullIfEmpty(funko.ppgPrice), isVaulted: funko.isVaulted, isChase: funko.isChase };
    }
    try {
      const saved = activeItem
        ? await updateItem.mutateAsync({ id: activeItem.id, payload: { ...payload, expectedVersion: baseVersion } })
        : await createItem.mutateAsync(payload);
      setBaseVersion(saved.version);
      if (!activeItem) setCreatedItem(saved);
      void queryClient.invalidateQueries({ queryKey: ["funko", saved.id] });

      if (!activeItem && pendingImage) {
        try {
          setUploading(true);
          const { imageUrls, version } = await uploadItemImage(saved.id, pendingImage);
          setBaseVersion(previous => previous !== undefined && version === previous + 1 ? version : previous);
          update("images", imageUrls);
          setPendingImage(null);
          void queryClient.invalidateQueries({ queryKey: ["items"] });
          void queryClient.invalidateQueries({ queryKey: ["item", saved.id] });
        } catch (error) {
          setUploadError(error instanceof Error ? error.message : "Image upload failed.");
          return;
        } finally {
          setUploading(false);
        }
      }

      void queryClient.invalidateQueries({ queryKey: ["item-suggestions"] });

      dirty.current = false;
      itemDrafts.delete(draftKey);
      if (guided && addAnother) {
        setCreatedItem(null);
        setForm(toForm(null, categories));
        setParentCategoryId(flatCategories.find((entry) => entry.id === defaultInventoryCategoryId(categories))?.parentId ?? "");
        setFunko(EMPTY_FUNKO);
        setFunkoExists(false);
        setWhatnot(toWhatnot(undefined));
        setPendingImage(null);
        setUploadError(null);
        setCreateStep(0);
        return;
      }

      onClose();
    } catch {
      // Surfaced through mutationError below.
    }
  }

  return (
    <Modal title={activeItem ? `Edit: ${activeItem.name}` : "New item"} onClose={requestClose} wide>
      <form onChangeCapture={() => { dirty.current = true; }} onSubmit={handleSubmit} className="space-y-3">
        {validationError ? <ErrorBanner message={validationError} /> : null}
        {mutationError ? <ErrorBanner message={(mutationError as Error).message} /> : null}
        {conflicted && activeItem ? <Card>
          <p className="text-sm text-amber-200">Your draft is unchanged. Compare it with the latest saved item before retrying.</p>
          <Button type="button" variant="secondary" onClick={() => {
            void api.get<{ data: Item }>(`/items/${activeItem.id}`).then(result => setLatestItem(result.data)).catch(error => setValidationError((error as Error).message));
          }}>Compare latest saved values</Button>
          {latestItem ? <><dl className="my-3 space-y-2 text-sm">{Object.entries(toForm(latestItem, categories)).filter(([key, value]) => JSON.stringify(value) !== JSON.stringify(form[key as keyof FormState])).map(([key, value]) => <div key={key}><dt className="font-semibold">{key.replace(/([A-Z])/g, " $1")}</dt><dd className="break-words text-slate-300">Latest: {typeof value === "object" ? JSON.stringify(value) : String(value || "Empty")}</dd></div>)}</dl>
          <Button type="button" variant="secondary" onClick={() => {
            if (!window.confirm("Keep your draft and save it over the newer values you reviewed?")) return;
            setBaseVersion(latestItem.version); updateItem.reset(); setLatestItem(null);
          }}>Keep my draft for the next save</Button></> : null}
        </Card> : null}
        {guided ? <div className="mb-4"><div className="flex items-center gap-2 overflow-x-auto pb-1">{steps.map((label,index)=><button key={label} type="button" onClick={()=>index < createStep && setCreateStep(index)} className={"whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium "+(index===createStep?"border-indigo-500 bg-indigo-950 text-indigo-200":index<createStep?"border-slate-700 text-slate-300":"border-slate-800 text-slate-400")}>{index+1}. {label}</button>)}</div><p className="mt-2 text-xs text-slate-400">Step {createStep+1} of {steps.length}</p></div> : null}

        {showStep(0) ? <div className="space-y-3">
        <Section title="General">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Name" className="sm:col-span-2">
              <Input
                value={form.name}
                onChange={(event) => update("name", event.target.value)}
                placeholder="e.g. Batman"
              />
            </Field>
            <Field label="Category">
              <Select value={parentCategoryId} onChange={(event) => changeCategory(event.target.value, event.target.value)}>
                <option value="">Uncategorised</option>
                {flatCategories.filter((category) => !category.parentId).map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
              </Select>
            </Field>
            <Field label="Subcategory">
              <Select value={selectedCategory?.parentId ? form.categoryId : ""} onChange={(event) => changeCategory(event.target.value || parentCategoryId, parentCategoryId)} disabled={!parentCategoryId}>
                <option value="">Choose a subcategory</option>
                {flatCategories.filter((category) => category.parentId === parentCategoryId).map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
                {selectedCategory?.parentId && selectedCategory.parentId !== parentCategoryId ? <option value={selectedCategory.id}>{selectedCategory.name}</option> : null}
              </Select>
            </Field>
            <Field label="Condition">
              <Select
                value={form.condition}
                onChange={(event) => update("condition", event.target.value)}
              >
                <option value="">Unspecified</option>
                {form.condition && !conditionOptions.includes(form.condition) ? <option value={form.condition}>{form.condition} (choose an accepted condition)</option> : null}
                {conditionOptions.map((condition) => (
                  <option key={condition} value={condition}>
                    {condition}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Hazmat" hint="Used by marketplace exports">
              <Select value={form.hazmat} onChange={(event) => update("hazmat", event.target.value)}>
                {ITEM_HAZMAT_VALUES.map((value) => <option key={value}>{value}</option>)}
              </Select>
            </Field>
            <Field label="Barcode" hint="Manufacturer UPC/EAN/ISBN printed on the box">
              <Input
                value={form.barcode}
                onChange={(event) => update("barcode", event.target.value)}
              />
            </Field>
            <Field label="SKU" hint="Your own stock code, if you use one">
              <Input value={form.sku} onChange={(event) => update("sku", event.target.value)} />
            </Field>
            <Field label="Description" className="sm:col-span-2">
              <Textarea
                value={form.description}
                onChange={(event) => update("description", event.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section title="Images" defaultOpen={guided}>
          <div className="space-y-2">
            <p className="text-xs text-slate-400">
              Images from approved hosts are copied after saving. If a URL remains, save again to retry.
            </p>
            {activeItem ? (
              <div className="flex flex-wrap items-center gap-3">
                <label
                  className={
                    "inline-flex cursor-pointer items-center rounded-lg border border-slate-700 px-3 py-1.5 text-xs font-medium text-slate-200 hover:border-slate-500 " +
                    (uploading ? "pointer-events-none opacity-60" : "")
                  }
                >
                  <input
                    type="file"
                    aria-label="Upload item photo"
                    accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
                    className="block w-full text-sm text-slate-300"
                    disabled={uploading}
                    onChange={handleUpload}
                  />
                  {uploading ? "Uploading…" : "Upload image"}
                </label>
                <span className="text-xs text-slate-400">
                  JPEG, PNG, WebP, GIF or AVIF · 8 MB max · stored as 800px WebP
                </span>
              </div>
            ) : (
              <p className="text-xs text-slate-400">
                {pendingImage ? `${pendingImage.name} will upload when you save.` : "Choose an optional photo; it uploads when you save the item."}
              </p>
            )}
            {!activeItem ? (
              <input
                type="file"
                    aria-label="Upload item photo"
                accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
                onChange={handleUpload}
                className="block w-full text-sm text-slate-300 file:mr-3 file:rounded file:border-0 file:bg-slate-800 file:px-3 file:py-2 file:text-slate-100"
              />
            ) : null}
            {uploadError ? <p className="text-xs text-rose-400">{uploadError}</p> : null}
            {form.images.map((url, index) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  value={url}
                  onChange={(event) =>
                    update(
                      "images",
                      form.images.map((entry, i) => (i === index ? event.target.value : entry)),
                    )
                  }
                  placeholder="https://…"
                />
                {url ? (
                  <img
                    src={activeItem && url === activeItem.imageUrls[index]
                      ? `/api/items/${activeItem.id}/images/${index}`
                      : url}
                    alt=""
                    className="h-9 w-9 shrink-0 rounded object-cover"
                    onError={(event) => {
                      (event.target as HTMLImageElement).style.visibility = "hidden";
                    }}
                  />
                ) : null}
                {url ? (
                  <span className="shrink-0 text-xs text-slate-400">
                    {url.startsWith("items/") ? "Stored" : "Pending"}
                  </span>
                ) : null}
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() =>
                    update(
                      "images",
                      form.images.filter((_entry, i) => i !== index),
                    )
                  }
                >
                  ✕
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="secondary"
              onClick={() => update("images", [...form.images, ""])}
            >
              Add image URL
            </Button>
          </div>
        </Section>

        {showFunko ? <Section title="Funko details" defaultOpen>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="License"><AutocompleteInput value={form.license} onChange={(value) => update("license", value)} options={licenseSuggestions.data?.license ?? suggestions.license} placeholder="e.g. Marvel" /></Field>
            <Field label="Series"><AutocompleteInput value={form.series} onChange={(value) => update("series", value)} options={seriesSuggestions.data?.series ?? suggestions.series} placeholder="e.g. Pop! Heroes" /></Field>
            <Field label="Box number">
              <Input
                value={form.boxNo}
                onChange={(event) => update("boxNo", event.target.value)}
                placeholder="e.g. 01"
              />
            </Field>
            <Field label="Exclusive to"><AutocompleteInput value={form.exclusiveTo} onChange={(value) => update("exclusiveTo", value)} options={retailerSuggestions.data?.exclusiveTo ?? suggestions.exclusiveTo} placeholder="Choose a saved retailer or type a new one" /><p className="text-xs text-slate-400">New retailers are added to suggestions when you save the item.</p></Field>
            <div className="sm:col-span-2">
              <FeatureMultiSelect values={funko.features} onChange={features => { dirty.current = true; setFunko(previous => ({ ...previous, features })); }} />
            </div>
            <Field label="PPG price">
              <Input
                inputMode="decimal"
                value={funko.ppgPrice}
                onChange={(event) => setFunko({ ...funko, ppgPrice: event.target.value })}
                placeholder="0.00"
              />
            </Field>
            <div className="flex items-end gap-4 pb-1">
              <Checkbox
                label="Chase"
                checked={funko.isChase}
                onChange={(event) => setFunko({ ...funko, isChase: event.target.checked })}
              />
              <Checkbox
                label="Vaulted"
                checked={funko.isVaulted}
                onChange={(event) => setFunko({ ...funko, isVaulted: event.target.checked })}
              />
            </div>
          </div>
        </Section> : null}

        </div> : null}

        {showStep(1) ? <div className="space-y-3">
        <Section title="Storage & stock">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Location"><AutocompleteInput value={form.location} onChange={(value) => update("location", value)} options={locationSuggestions.data?.location ?? suggestions.location} placeholder="e.g. Bin A1" /></Field>
            <Field label="Owner"><Input value={form.owner} maxLength={200} onChange={(event) => update("owner", event.target.value)} placeholder="e.g. Personal, consignment, or client" /></Field>
            <Field label="Shipping weight" hint="Also used as the Whatnot shipping profile">
              <Select
                value={form.popWeight}
                onChange={(event) => update("popWeight", event.target.value)}
              >
                <option value="">Unspecified</option>
                {POP_WEIGHTS.map((weight) => (
                  <option key={weight} value={weight}>
                    {weight}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Quantity">
              <Input
                type="number"
                min={0}
                value={form.quantity}
                onChange={(event) => update("quantity", event.target.value)}
              />
            </Field>
            <Field label="Last checked">
              <Input
                type="date"
                value={form.lastChecked}
                onChange={(event) => update("lastChecked", event.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section title="Valuation">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Purchase price">
              <Input
                inputMode="decimal"
                value={form.purchasePrice}
                onChange={(event) => update("purchasePrice", event.target.value)}
                placeholder="0.00"
              />
            </Field>
            <Field label="Current value">
              <Input
                inputMode="decimal"
                value={form.currentValue}
                onChange={(event) => update("currentValue", event.target.value)}
                placeholder="0.00"
              />
            </Field>
            <Field label="Asking price">
              <Input
                inputMode="decimal"
                value={form.forSalePrice}
                onChange={(event) => update("forSalePrice", event.target.value)}
                placeholder="0.00"
              />
            </Field>
          </div>
        </Section>

        </div> : null}

        {showStep(2) ? <div className="space-y-3">
        <Section title="Whatnot listing" defaultOpen={false}>
          <p className="text-xs text-slate-400">Category, subcategory, condition, Hazmat and shipping weight come from the inventory item.</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Listing type">
              <Select value={whatnot.type} onChange={(event) => setWhatnot({ ...whatnot, type: event.target.value })}>
                {["Buy it Now", "Auction"].map((value) => <option key={value}>{value}</option>)}
              </Select>
            </Field>
            <Field label="Offerable">
              <Select value={whatnot.offerable} onChange={(event) => setWhatnot({ ...whatnot, offerable: event.target.value })}>
                <option value="TRUE">Yes</option><option value="FALSE">No</option>
              </Select>
            </Field>
          </div>
        </Section>

        <Section title="Sale" defaultOpen={false}>
          <div className="space-y-3">
            <p className="text-xs text-slate-400">
              Items are available until you mark them sold. Listings below record where an available
              item has been posted.
            </p>

            {form.listings.map((listing, index) => (
              <div key={index} className="flex gap-2">
                <Select
                  value={listing.platform}
                  onChange={(event) => updateListing(index, { platform: event.target.value })}
                  className="w-40"
                >
                  <option value="">Platform…</option>
                  {SALE_PLATFORMS.map((platform) => (
                    <option key={platform} value={platform}>
                      {platform}
                    </option>
                  ))}
                </Select>
                <Input
                  value={listing.url}
                  onChange={(event) => updateListing(index, { url: event.target.value })}
                  placeholder="https://…"
                />
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() =>
                    update(
                      "listings",
                      form.listings.filter((_entry, i) => i !== index),
                    )
                  }
                >
                  ✕
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="secondary"
              onClick={() => update("listings", [...form.listings, { platform: "", url: "" }])}
            >
              Add listing
            </Button>

            <div className="border-t border-slate-800 pt-3">
              <Checkbox
                label="Sold"
                checked={form.sold}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setForm((previous) => ({
                    ...previous,
                    sold: checked,
                    soldDate: checked && !previous.soldDate ? today() : previous.soldDate,
                  }));
                }}
              />
              {form.sold ? (
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <Field label="Sold date">
                    <Input
                      type="date"
                      value={form.soldDate}
                      onChange={(event) => update("soldDate", event.target.value)}
                    />
                  </Field>
                  <Field label="Sold where">
                    <Select
                      value={form.soldWhere}
                      onChange={(event) => update("soldWhere", event.target.value)}
                    >
                      <option value="">Unspecified</option>
                      {SALE_PLATFORMS.map((platform) => (
                        <option key={platform} value={platform}>
                          {platform}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="Sold price">
                    <Input type="number" min="0" step="0.01" value={form.soldPrice} onChange={(event) => update("soldPrice", event.target.value)} placeholder="0.00" />
                  </Field>
                </div>
              ) : null}
            </div>
          </div>
        </Section>

        </div> : null}

        {showStep(3) ? <div className="space-y-3"><Card><h3 className="font-semibold text-slate-100">{form.name.trim() || "Unnamed item"}</h3><div className="mt-3 grid gap-2 text-sm sm:grid-cols-2"><div><span className="text-slate-400">Category</span><div className="text-slate-200">{[categoryPair.category, categoryPair.subCategory].filter(Boolean).join(" → ") || "Uncategorised"}</div></div><div><span className="text-slate-400">Collection</span><div className="text-slate-200">{[form.license,form.series].filter(Boolean).join(" · ") || "—"}</div></div><div><span className="text-slate-400">Quantity / location</span><div className="text-slate-200">{form.quantity} · {form.location || "No location"}</div></div><div><span className="text-slate-400">Value / asking</span><div className="text-slate-200">{form.currentValue || "—"} / {form.forSalePrice || "—"}</div></div></div></Card></div> : null}

        <div className="sticky bottom-0 -mx-1 flex flex-wrap items-center justify-between gap-3 border-t border-slate-800 bg-slate-950/95 px-1 pt-3">
          <div>{guided && createStep > 0 ? <Button type="button" variant="ghost" onClick={()=>setCreateStep(step=>step-1)}>Back</Button> : null}</div>
          <div className="flex gap-2">
            {activeItem && showFunko ? <Button type="button" variant="secondary" onClick={() => setShowGtPicker(true)} disabled={busy}>{activeItem.gtPoppediaUuid ? "Update Funko Metadata" : "Fill from Funko Metadata"}</Button> : null}
            {activeItem?.gtPoppediaUuid ? <Button type="button" variant="secondary" onClick={() => refreshValuation.mutate(activeItem.id)} disabled={busy || refreshValuation.isPending}>{refreshValuation.isPending ? "Refreshing…" : "Refresh valuation"}</Button> : null}
            <Button type="button" variant="secondary" onClick={requestClose} disabled={busy}>Cancel</Button>
            {guided && createStep < steps.length - 1 ? <Button type="button" disabled={createStep===0 && !form.name.trim()} onClick={()=>setCreateStep(step=>step+1)}>Continue</Button> : <>
              {guided ? <Button type="submit" data-action="add-another" variant="secondary" disabled={busy}>{busy ? "Saving…" : "Save & add another"}</Button> : null}
              <Button type="submit" disabled={busy}>{busy ? "Saving…" : activeItem ? "Save changes" : "Create item"}</Button>
            </>}
          </div>
        </div>
      </form>

      {showGtPicker && activeItem ? (
        <GtPoppediaPicker
          item={activeItem}
          onClose={() => setShowGtPicker(false)}
          onApplied={() => {
            setShowGtPicker(false);
          }}
        />
      ) : null}
    </Modal>
  );
}
